import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { addQuestion } from "../../../store/reducer/questions/questionSlice";

function AddQuestion() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [newOption, setNewOption] = useState("");

  const validationSchema = Yup.object().shape({
    question_text: Yup.string().required("Please enter the question text"),
    response_type: Yup.string().required("Please select a response type"),
    options: Yup.array().when("response_type", (response_type, schema) => {
      return response_type === "multiple_choice"
        ? schema.min(2, "Please add at least two options")
        : schema.notRequired();
    }),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const payload = {
      question_text: values.question_text,
      response_type: values.response_type,
      options: values.response_type === "multiple_choice" ? values.options : [],
      createdBy: JSON.parse(localStorage.getItem("userSession"))?.id,
    };

    await dispatch(addQuestion(payload)).then((result) => {
      if (result.payload) {
        resetForm();
        toast.success("Question added successfully!");
      } else {
        toast.error(result.error.message);
      }
      setSubmitting(false);
    });
  };

  return (
    <>
      <div className="flex justify-between mb-[30px]">
        <div className="flex gap-[16px] items-center">
          <Link to="/setting/drawing-questions">
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] leading-[16px] text-[#9E9E9E] text-[14px]">
              Settings / Questions
            </span>
            <span className="font-Inter font-[700] leading-[30px] text-[24px]">
              Add Question
            </span>
          </div>
        </div>
      </div>

      <Formik
        initialValues={{
          question_text: "",
          response_type: "text",
          options: [],
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <div className="md:grid md:grid-cols-2 gap-[20px] bg-[#F8F8F8] rounded-[12px] p-4 w-full">
              <div className="mb-2">
                <label
                  className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                  htmlFor="question_text"
                >
                  Question Text
                </label>
                <Field
                  type="text"
                  name="question_text"
                  className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  placeholder="Enter question text"
                  onChange={(e) => setFieldValue("question_text", e.target.value)}
                />
                {errors.question_text && touched.question_text ? (
                  <div className="text-red-700 text-[12px] mt-1">{errors.question_text}</div>
                ) : null}
              </div>

              <div className="mb-2">
                <label
                  className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                  htmlFor="response_type"
                >
                  Response Type
                </label>
                <Field
                  as="select"
                  name="response_type"
                  className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  onChange={(e) => {
                    setFieldValue("response_type", e.target.value);
                    if (e.target.value !== "multiple_choice") {
                      setFieldValue("options", []);
                    }
                  }}
                >
                  <option value="text">Text</option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="boolean">Boolean</option>
                </Field>
                {errors.response_type && touched.response_type ? (
                  <div className="text-red-700 text-[12px] mt-1">{errors.response_type}</div>
                ) : null}
              </div>

              {values.response_type === "multiple_choice" && (
                <div className="mb-4 col-span-2">
                  <label
                    className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                    htmlFor="options"
                  >
                    Options
                  </label>
                  <div className="flex gap-[10px] mb-[12px] max-w-[600px]">
                    <input
                      type="text"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      className="block w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      placeholder="Enter option"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newOption.trim()) {
                          setFieldValue("options", [...values.options, newOption.trim()]);
                          setNewOption("");
                        }
                      }}
                      className="py-[8px] px-[10px] bg-[#1C1C20] text-white font-Inter font-[500] rounded-[12px] whitespace-nowrap"
                    >
                      Add Option
                    </button>
                  </div>
                  {errors.options && touched.options ? (
                    <div className="text-red-700 text-[12px] mb-2">{errors.options}</div>
                  ) : null}
                  <div className="w-[600px] h-[200px] bg-white rounded-[12px] p-4 overflow-y-auto">
                    {values.options.length === 0 ? (
                      <p className="text-gray-500 font-Inter text-[14px]">No options added yet.</p>
                    ) : (
                      <ul className="space-y-2">
                        {values.options.map((option, index) => (
                          <li
                            key={index}
                            className="flex justify-between items-center bg-[#F8F8F8] rounded-[8px] px-3 py-2 text-gray-700 font-Inter text-[14px]"
                          >
                            <span className="truncate">{option}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setFieldValue(
                                  "options",
                                  values.options.filter((_, i) => i !== index)
                                )
                              }
                              className="text-red-500 font-Inter font-[500] hover:underline"
                            >
                              Delete
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-[24px] bg-[#F8F8F8] rounded-[12px] mt-5 p-4 justify-end">
              <Link to="/setting/drawing-questions">
                <button
                  type="button"
                  className="py-[6px] px-[30px] text-center text-[#1C1C20] bg-[#F8F8F8] border-2 border-[#1C1C20] rounded-[12px] text-[18px] font-Inter font-[500]"
                >
                  Cancel
                </button>
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`py-[6px] px-[30px] text-center text-white rounded-[12px] text-[18px] font-Inter font-[500] ${
                  isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                }`}
              >
                {isSubmitting ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  "Add Question"
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default AddQuestion;